import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const DEFAULT_SOUVENIR_CATEGORIES = ['Магниты', 'Открытки', 'Книги', 'Текстиль', 'Игрушки', 'Прочее'];

const CATEGORY_EMOJIS = {
    Магниты: '🧲',
    Открытки: '📮',
    Книги: '📚',
    Текстиль: '🧣',
    Игрушки: '🧸',
    Прочее: '🎁'
};

function SouvenirsPanel({ token }) {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [filterCategory, setFilterCategory] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        category: '',
        sku: '',
        is_available: true
    });

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    const categoryOptions = categories.length > 0 ? categories : DEFAULT_SOUVENIR_CATEGORIES;

    const fetchItems = async () => {
        try {
            const url = filterCategory
                ? `${API_URL}/api/souvenirs/items?category=${encodeURIComponent(filterCategory)}`
                : `${API_URL}/api/souvenirs/items`;
            const response = await apiFetch(url);
            const data = await response.json();
            if (data.success) {
                setItems(data.items || []);
            }
        } catch (error) {
            console.error('Error fetching souvenirs:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/settings/options/souvenir_categories`);
            const data = await response.json();
            if (data.success) {
                setCategories((data.options || []).map((option) => option.label || option.value));
            }
        } catch (error) {
            console.error('Error fetching souvenir categories:', error);
        }
    };

    useEffect(() => {
        fetchItems();
        fetchCategories();
    }, [filterCategory]);

    const handleOpenDialog = () => {
        setEditingItem(null);
        setSaveError('');
        setFormData({
            name: '',
            description: '',
            price: '',
            category: '',
            sku: '',
            is_available: true
        });
        setOpenDialog(true);
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setSaveError('');
        setFormData({
            name: item.name,
            description: item.description || '',
            price: item.price,
            category: item.category || '',
            sku: item.sku || '',
            is_available: item.is_available
        });
        setOpenDialog(true);
    };

    const handleSaveItem = async () => {
        const name = formData.name.trim();
        const category = formData.category.trim();
        const price = parseFloat(formData.price);

        if (!name) {
            setSaveError('Укажите название');
            return;
        }
        if (!category) {
            setSaveError('Выберите категорию');
            return;
        }
        if (Number.isNaN(price) || price < 0) {
            setSaveError('Укажите корректную цену');
            return;
        }

        setSaving(true);
        setSaveError('');

        try {
            const url = editingItem
                ? `${API_URL}/api/souvenirs/items/${editingItem.id}`
                : `${API_URL}/api/souvenirs/items`;
            const method = editingItem ? 'PUT' : 'POST';
            const payload = {
                name,
                description: formData.description?.trim() || null,
                price,
                category,
                sku: formData.sku?.trim() || null,
                is_available: formData.is_available !== false
            };

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                setSaveError(data.error || 'Не удалось сохранить');
                return;
            }

            await apiFetch(`${API_URL}/api/settings/options/souvenir_categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: category, label: category })
            });

            setOpenDialog(false);
            fetchItems();
            fetchCategories();
        } catch (error) {
            console.error('Error saving souvenir:', error);
            setSaveError('Ошибка сети при сохранении');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async (id) => {
        if (!window.confirm('Удалить эту позицию?')) return;
        try {
            const response = await apiFetch(`${API_URL}/api/souvenirs/items/${id}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                fetchItems();
            }
        } catch (error) {
            console.error('Error deleting souvenir:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Сувенирка</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Справочник сувениров</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
                    Добавить сувенир
                </Button>
            </Box>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Категория</InputLabel>
                                <Select
                                    value={filterCategory}
                                    label="Категория"
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    <MenuItem value="">Все категории</MenuItem>
                                    {categoryOptions.map((cat) => (
                                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={`Всего: ${items.length}`} color="primary" />
                                <Chip label={`В продаже: ${items.filter((i) => i.is_available).length}`} color="success" />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Артикул</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Категория</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Цена</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <TableRow key={item.id} hover>
                                <TableCell>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                                    {item.description && (
                                        <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>{item.sku || '—'}</TableCell>
                                <TableCell>
                                    <Chip label={item.category || '—'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                        ₽{parseFloat(item.price).toLocaleString('ru-RU')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.is_available ? 'В продаже' : 'Снято'}
                                        size="small"
                                        color={item.is_available ? 'success' : 'default'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => handleEditItem(item)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {items.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography color="text.secondary">Нет позиций в справочнике</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenDialog} sx={{ mt: 2 }}>
                        Добавить первый сувенир
                    </Button>
                </Box>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Редактировать сувенир' : 'Добавить сувенир'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {saveError && (
                            <Alert severity="error" onClose={() => setSaveError('')}>{saveError}</Alert>
                        )}

                        <TextField
                            label="Название"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                            required
                        />

                        <TextField
                            label="Описание"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                        />

                        <TextField
                            label="Артикул / SKU"
                            value={formData.sku}
                            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                            fullWidth
                        />

                        <TextField
                            label="Цена (₽)"
                            type="number"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            fullWidth
                            required
                            inputProps={{ min: 0, step: 0.01 }}
                        />

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Категория *
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {categoryOptions.map((cat) => (
                                    <Button
                                        key={cat}
                                        type="button"
                                        variant={formData.category === cat ? 'contained' : 'outlined'}
                                        onClick={() => setFormData({ ...formData, category: cat })}
                                        sx={{
                                            minWidth: 72,
                                            width: 72,
                                            height: 72,
                                            p: 0.5,
                                            flexDirection: 'column',
                                            borderRadius: 1.5,
                                            textTransform: 'none'
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontSize: '1.25rem', mb: 0.25 }}>
                                            {CATEGORY_EMOJIS[cat] || '🎁'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center' }}>
                                            {cat}
                                        </Typography>
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Статус</InputLabel>
                            <Select
                                value={formData.is_available}
                                label="Статус"
                                onChange={(e) => setFormData({ ...formData, is_available: e.target.value })}
                            >
                                <MenuItem value={true}>В продаже</MenuItem>
                                <MenuItem value={false}>Снято с продажи</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} disabled={saving}>Отмена</Button>
                    <Button type="button" variant="contained" onClick={handleSaveItem} disabled={saving}>
                        {saving ? 'Сохранение...' : (editingItem ? 'Сохранить' : 'Добавить')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SouvenirsPanel;
